// website/components/ResultsLabTab.jsx
// Admin-only "Results Lab" — vacuum test harness for the display layer.
// Loads a real-data fixture, runs the REAL useFilteredAndSortedOptions hook
// against it with stubbed context, and renders the REAL NewResultsView.
// Below it, a Sort Key Inspector shows the computed sort key per card plus
// Verify buttons that run assertion passes. No network, no cache hydration.

import React, { useMemo, useState, useCallback, useRef } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Platform,
  ActivityIndicator, useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import NewResultsView from './NewResultsView';
import useSgpFilterState from '../hooks/useSgpFilterState';
import { useFilteredAndSortedOptions } from '../hooks/useFilteredAndSortedOptions';
import {
  normalizeQuery, getMarketRelevanceToQuery,
  _extractNamesFromSelection, _isOptionPlayerProp, extractResultFacets,
} from '../utils/sgpFlowUtils';
import { cleanPerBookEv } from '../utils/canonicalEv';
import {
  loadFixtureFromObject, loadE2EQueryFixture, formatQueryLabel,
} from '../utils/resultsLabFixtures';
import {
  verifySortOrder, verifyFilters, HANDLED_SORT_MODES,
} from '../utils/resultsLabVerify';
import ResultsLabInspector from './ResultsLabInspector';
import LabHeader from './LabHeader';
import { uiVar } from './floodlight/uiStyleCss';
import { ADM_DISPLAY } from './floodlight/vars/admin';

// Stubs for component-scope helpers — mirror useSgpFlowData.js inline defs.
function makeSortEvForBook(consensusOptions) {
  return (raw, bookData) => {
    if (!bookData) return null;
    const isSGPItem = raw?.ev_method === 'individual_leg_devig';
    if (isSGPItem) {
      const ev = cleanPerBookEv(raw, bookData, true, consensusOptions);
      if (ev !== null) return ev;
      if (typeof bookData === 'object' && typeof bookData.ev_percent === 'number') return bookData.ev_percent;
      return null;
    }
    return cleanPerBookEv(raw, bookData, false, consensusOptions);
  };
}

function makeGetMyBooksBest(myBooksList, sortBy, _wantsMyBooksMode, consensusOptions) {
  return (bookOdds, rawForEv = null) => {
    const bo = bookOdds || {};
    const wantMyBooks = (_wantsMyBooksMode || sortBy === 'mybooks') && myBooksList.length > 0;
    if (wantMyBooks) {
      let bestBook = null, bestEv = -Infinity, bestPrice = -Infinity, hadEv = false;
      const _sortEvForBook = makeSortEvForBook(consensusOptions);
      for (const [b, d] of Object.entries(bo)) {
        const bl = b.toLowerCase();
        const isMine = myBooksList.some(mb => bl === mb.toLowerCase() || bl.includes(mb.toLowerCase()));
        if (!isMine) continue;
        let ev = null;
        if (rawForEv) {
          try {
            const cpe = _sortEvForBook(rawForEv, d);
            if (Number.isFinite(cpe)) ev = cpe;
          } catch { /* ignore */ }
        }
        if (ev == null && Number.isFinite(d?.ev_percent)) ev = d.ev_percent;
        if (ev != null && Number.isFinite(ev)) {
          hadEv = true;
          if (ev > bestEv) { bestEv = ev; bestBook = b; }
        } else if (!hadEv) {
          const price = d?.price;
          if (typeof price === 'number' && price > bestPrice) { bestPrice = price; bestBook = b; }
        }
      }
      return bestBook;
    }
    let best = null, bestPrice = -Infinity;
    for (const [b, d] of Object.entries(bo)) {
      const price = d?.price;
      if (typeof price === 'number' && price > bestPrice) { bestPrice = price; best = b; }
    }
    return best;
  };
}

const LAB_SORT_OPTIONS = [
  { value: 'ev',          label: 'Best EV' },
  { value: 'odds',        label: 'Best Odds (odds → price_desc)' },
  { value: 'price_desc',  label: 'Best Odds (price_desc)' },
  { value: 'price_asc',   label: 'Lowest Odds (price_asc)' },
  { value: 'date',        label: 'By Date' },
  { value: 'sportsbooks', label: 'Most Books' },
  { value: 'market',      label: 'Market A–Z' },
  { value: 'mybooks',     label: 'My Books EV' },
  { value: 'relevance',   label: 'Relevance (backend order)' },
];

export default function ResultsLabTab() {
  const { width: screenWidth } = useWindowDimensions();
  const fileInputRef = useRef(null);

  const [fixture, setFixture] = useState(null);
  const [queryIndex, setQueryIndex] = useState(0);
  const [fixturePath, setFixturePath] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Capture-fresh state (live nlOdds query → fixture)
  const [capturePrompt, setCapturePrompt] = useState('best bets tonight');
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState(null);

  const [myBooksInput, setMyBooksInput] = useState('DraftKings, FanDuel, BetMGM');
  const [defaultBookSort, setDefaultBookSort] = useState('ev');
  const [booksViewOverride, setBooksViewOverride] = useState(null);
  const [preferredSportsbook, setPreferredSportsbook] = useState(null);
  const [referenceTime, setReferenceTime] = useState(null);
  const [disableMinBooksFilter, setDisableMinBooksFilter] = useState(true);
  const [disableMyBooksPriority, setDisableMyBooksPriority] = useState(false);
  const [disableDateKeywordFilter, setDisableDateKeywordFilter] = useState(true);
  const [disableRelevanceFilter, setDisableRelevanceFilter] = useState(true);

  const myBooks = useMemo(
    () => myBooksInput.split(',').map(s => s.trim()).filter(Boolean),
    [myBooksInput]
  );
  const userProfile = useMemo(() => ({
    preferences: {
      myBooks, defaultBookSort,
      defaultSortMethod: 'ev',
      minBooksStraights: 1, minBooksSGP: 2, minBooksMyBooks: 1,
      filterPositiveEvOnly: false, legsPerPage: 10,
    },
  }), [myBooks, defaultBookSort]);

  const filterState = useSgpFilterState({ userProfile });
  const [sortBy, setSortBy] = useState('ev');
  const [currentPage, setCurrentPage] = useState(1);

  const _effectiveBooksSort = booksViewOverride ?? defaultBookSort;
  const _wantsMyBooksMode = myBooks.length > 0 && _effectiveBooksSort === 'mybooks';
  const _myBooksList = myBooks;

  const loadedQuery = useMemo(() => {
    if (!fixture?.data) return null;
    try {
      return loadFixtureFromObject(fixture.data, queryIndex);
    } catch (err) {
      return { __loadError: String(err?.message || err) };
    }
  }, [fixture, queryIndex]);

  const loadPipelineError = loadedQuery?.__loadError || null;
  const safeLoadedQuery = loadPipelineError ? null : loadedQuery;

  React.useEffect(() => {
    if (safeLoadedQuery?.referenceTime) {
      setReferenceTime(safeLoadedQuery.referenceTime);
    }
  }, [safeLoadedQuery?.referenceTime, queryIndex]);

  const fetchedOptions = safeLoadedQuery?.fetchedOptions || [];
  const myBooksFetchedOptions = [];
  const queryLabel = formatQueryLabel(safeLoadedQuery?.query, capturePrompt || '');
  const facets = useMemo(
    () => extractResultFacets(fetchedOptions, queryLabel),
    [fetchedOptions, queryLabel]
  );
  const availableSportsbooks = facets.availableSportsbooks;
  const availableMarkets = facets.availableMarkets;
  const availablePlayers = facets.availablePlayers;
  const availableLegCounts = facets.availableLegCounts;
  const consensusOptions = null;
  const _sortEvForBook = useMemo(() => makeSortEvForBook(consensusOptions), []);
  const _getMyBooksBest = useMemo(
    () => makeGetMyBooksBest(_myBooksList, sortBy, _wantsMyBooksMode, consensusOptions),
    [_myBooksList, sortBy, _wantsMyBooksMode]
  );

  const _labFilterArgs = {
    fetchedOptions, myBooksFetchedOptions,
    resultsContextQuery: queryLabel,
    responseMetadata: { _queryClassification: safeLoadedQuery?.classification || null },
    referenceTime: referenceTime || safeLoadedQuery?.referenceTime || null,
    didBroaden: false, preferredSportsbook,
    searchText: filterState.searchText,
    filterSportsbooks: filterState.filterSportsbooks,
    filterMarkets: filterState.filterMarkets,
    filterPlayers: filterState.filterPlayers,
    filterResultType: filterState.filterResultType,
    filterDeepLinksOnly: filterState.filterDeepLinksOnly,
    filterPositiveEvOnly: filterState.filterPositiveEvOnly,
    filterLineType: filterState.filterLineType,
    filterLegCount: filterState.filterLegCount,
    disableMinBooksFilter, disableMyBooksPriority, disableDateKeywordFilter, disableRelevanceFilter,
    sortBy, _effectiveBooksSort, _myBooksList,
    userProfile, consensusOptions,
    _extractNamesFromSelection, _getMyBooksBest, _isOptionPlayerProp, _sortEvForBook,
    getMarketRelevanceToQuery, normalizeQuery,
  };
  const filteredAllBooksOptions = useFilteredAndSortedOptions({
    ..._labFilterArgs,
    _wantsMyBooksMode: false,
  });
  const filteredMyBooksOptions = useFilteredAndSortedOptions({
    ..._labFilterArgs,
    _wantsMyBooksMode: true,
  });
  const filteredAndSortedOptions = _wantsMyBooksMode ? filteredMyBooksOptions : filteredAllBooksOptions;
  const _allBooksCount = filteredAllBooksOptions.length;
  const _myBooksCount = _myBooksList.length > 0 ? filteredMyBooksOptions.length : 0;
  const filterStages = (typeof window !== 'undefined' && window.__filterStageStats) || null;

  const itemsPerPage = userProfile.preferences.legsPerPage;
  const totalCount = fetchedOptions.length;
  const totalFilteredCount = filteredAndSortedOptions.length;
  const totalPages = Math.max(1, Math.ceil(totalFilteredCount / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedOptions = filteredAndSortedOptions.slice(
    (safePage - 1) * itemsPerPage, safePage * itemsPerPage
  );
  const onPageChange = useCallback((p) => setCurrentPage(Math.max(1, Math.min(p, totalPages))), [totalPages]);

  const [verifyResult, setVerifyResult] = useState(null);
  const runVerify = useCallback(() => {
    const sortRes = verifySortOrder(filteredAndSortedOptions, sortBy, { myBooks: _myBooksList, consensusOptions });
    const filterRes = verifyFilters({
      sorted: filteredAndSortedOptions, originalPool: fetchedOptions,
      filterState: {
        searchText: filterState.searchText,
        filterSportsbooks: filterState.filterSportsbooks,
        filterMarkets: filterState.filterMarkets,
        filterPlayers: filterState.filterPlayers,
        filterResultType: filterState.filterResultType,
        filterDeepLinksOnly: filterState.filterDeepLinksOnly,
        filterPositiveEvOnly: filterState.filterPositiveEvOnly,
        filterLineType: filterState.filterLineType,
      },
      consensusOptions,
    });
    setVerifyResult({ sort: sortRes, filter: filterRes, ts: Date.now() });
  }, [filteredAndSortedOptions, sortBy, _myBooksList, fetchedOptions, filterState]);

  const handleFilePick = useCallback(async (e) => {
    const file = e?.nativeEvent?.target?.files?.[0] || e?.target?.files?.[0];
    if (!file) return;
    setLoading(true); setLoadError(null);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const queries = Array.isArray(data?.results)
        ? data.results.map((r, i) => ({
            index: i,
            q: formatQueryLabel(r.q || r.prompt || r.query, `Query ${i + 1}`),
            count: r.items?.length || 0,
          }))
        : data?.items || data?.odds
          ? [{ index: 0, q: file.name, count: (data.items || data.odds || []).length }]
          : [];
      setFixture({ data, availableQueries: queries });
      setFixturePath(file.name);
      setQueryIndex(0); setCurrentPage(1); setVerifyResult(null); setReferenceTime(null);
    } catch (err) {
      setLoadError(String(err?.message || err));
    } finally { setLoading(false); }
  }, []);

  const handleLoadByPath = useCallback(async () => {
    const path = fixturePath.trim();
    if (!path || path.startsWith('(captured:')) {
      setLoadError('Load by path only works for files in website/public/. For scripts/output E2E files, use "Pick fixture file".');
      return;
    }
    setLoading(true); setLoadError(null);
    try {
      const loaded = await loadE2EQueryFixture(path, 0);
      const data = { results: [{ q: loaded.query, items: loaded.rawItems, classification: loaded.classification, referenceTime: loaded.referenceTime }] };
      const queries = [{ index: 0, q: loaded.query, count: loaded.rawItems.length }];
      setFixture({ data, availableQueries: queries });
      setQueryIndex(0); setCurrentPage(1); setVerifyResult(null); setReferenceTime(loaded.referenceTime || null);
    } catch (err) {
      setLoadError(String(err?.message || err));
    } finally { setLoading(false); }
  }, [fixturePath]);

  // Capture a fresh fixture from the live nlOdds endpoint. The response
  // shape matches the E2E files (results[].items[]), so we load it straight
  // into the same fixture slot. Uses the same FUNCTIONS_BASE_URL env the app
  // uses, so it hits the same backend the user-facing search hits.
  const handleCapture = useCallback(async () => {
    const prompt = capturePrompt.trim();
    if (!prompt) return;
    setCapturing(true); setCaptureError(null);
    try {
      const base = (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
        || (typeof window !== 'undefined' && window.__FUNCTIONS_BASE_URL__)
        || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort('capture timed out'), 60000);
      let res;
      try {
        res = await fetch(`${base}/nlOdds`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt, prefer: 'straight', defaults: {} }),
          signal: ctrl.signal,
        });
      } finally {
        clearTimeout(timer);
      }
      const text = await res.text();
      if (!res.ok) throw new Error(`nlOdds ${res.status}: ${text.slice(0, 200)}`);
      const data = JSON.parse(text);
      const itemCount = data?.results?.[0]?.items?.length
        || data?.items?.length
        || data?.odds?.length
        || 0;
      const queries = Array.isArray(data?.results) && data.results.length > 0
        ? data.results.map((r, i) => ({
            index: i,
            q: formatQueryLabel(r.q || r.prompt || r.query, prompt || `Query ${i + 1}`),
            count: r.items?.length || 0,
          }))
        : [{ index: 0, q: prompt, count: itemCount }];
      setFixture({ data, availableQueries: queries });
      setFixturePath(`(captured: ${prompt})`);
      setQueryIndex(0); setCurrentPage(1); setVerifyResult(null); setReferenceTime(null);
    } catch (err) {
      setCaptureError(String(err?.message || err));
    } finally { setCapturing(false); }
  }, [capturePrompt]);

  if (Platform.OS !== 'web') {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Results Lab</Text>
        <Text style={styles.muted}>Web-only. Open the admin panel in a browser.</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {renderHeader({ fileInputRef, handleFilePick, runVerify, hasData: !!fetchedOptions.length })}
      {renderLoaderRow({ fixturePath, setFixturePath, handleLoadByPath, loading, loadError })}
      {renderCaptureRow({ capturePrompt, setCapturePrompt, handleCapture, capturing, captureError })}
      {fixture?.availableQueries?.length > 0 && renderControls({
        fixture, queryIndex, setQueryIndex, setCurrentPage, setVerifyResult,
        myBooksInput, setMyBooksInput,
        booksViewOverride, setBooksViewOverride, defaultBookSort,
        preferredSportsbook, setPreferredSportsbook,
        referenceTime, setReferenceTime,
        disableMinBooksFilter, setDisableMinBooksFilter,
        disableMyBooksPriority, setDisableMyBooksPriority,
        disableDateKeywordFilter, setDisableDateKeywordFilter,
        disableRelevanceFilter, setDisableRelevanceFilter,
        sortBy, setSortBy,
      })}
      {renderSortWarning({ sortBy })}
      {renderPipelineStats({
        loadedQuery: safeLoadedQuery, loadPipelineError, referenceTime,
        totalFilteredCount, filterStages,
      })}
      {renderVerifyPanel({ verifyResult })}
      <View style={styles.previewArea}>
        <Text style={styles.sectionLabel}>User-facing view (REAL NewResultsView)</Text>
        <View style={styles.previewBox}>
          <NewResultsView
            searchText={filterState.searchText} setSearchText={filterState.setSearchText}
            filterSportsbooks={filterState.filterSportsbooks} setFilterSportsbooks={filterState.setFilterSportsbooks}
            filterMarkets={filterState.filterMarkets} setFilterMarkets={filterState.setFilterMarkets}
            filterPlayers={filterState.filterPlayers} setFilterPlayers={filterState.setFilterPlayers}
            filterResultType={filterState.filterResultType} setFilterResultType={filterState.setFilterResultType}
            filterDeepLinksOnly={filterState.filterDeepLinksOnly} setFilterDeepLinksOnly={filterState.setFilterDeepLinksOnly}
            filterPositiveEvOnly={filterState.filterPositiveEvOnly} setFilterPositiveEvOnly={filterState.setFilterPositiveEvOnly}
            filterLineType={filterState.filterLineType} setFilterLineType={filterState.setFilterLineType}
            filterLegCount={filterState.filterLegCount} setFilterLegCount={filterState.setFilterLegCount}
            sortBy={sortBy} setSortBy={setSortBy}
            availableSportsbooks={availableSportsbooks}
            availableMarkets={availableMarkets}
            availablePlayers={availablePlayers}
            availableLegCounts={availableLegCounts}
            _effectiveBooksSort={_effectiveBooksSort}
            setBooksViewOverride={setBooksViewOverride}
            _allBooksCount={_allBooksCount}
            _myBooksCount={_myBooksCount}
            lineTypeCounts={filteredAndSortedOptions?.lineTypeCounts}
            paginatedCards={[]}
            paginatedOptions={paginatedOptions}
            isGrouped={false}
            totalFilteredCount={totalFilteredCount}
            totalCount={totalCount}
            renderableCount={totalFilteredCount}
            currentPage={safePage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            isLiveQuery={false}
            pendingQuery={queryLabel}
            queryFeedback={null}
            handleQueryFeedback={() => {}}
            cardSharedProps={{
              requestedLine: null,
              requestedDirection: null,
              selectedLegs: [],
              handleAddFetchedLeg: () => {},
              openDeepLink: () => {},
              handleWatchFixture: () => {},
              handleUnwatchFixture: () => {},
              watchedFixtures: new Set(),
              canShowMath: true,
              showAllMath: true,
              isAdmin: true,
              userProfile,
              filterSportsbooks: filterState.filterSportsbooks,
              _effectiveBooksSort,
              preferredSportsbook,
              _wantsMyBooksMode,
              _myBooksList,
              _getMyBooksBest,
              getMarketPeers: () => [],
              getFixtureLogosFromTicker: () => null,
              getTeamLogo: () => null,
              sortBy,
            }}
            selectedLegs={[]}
            onSaveBetSlip={() => {}}
            computeCap={() => 3}
            showAllMathOverride={true}
            onToggleShowAllMath={() => {}}
            screenWidth={screenWidth}
          />
        </View>
      </View>
      <ResultsLabInspector
        sorted={filteredAndSortedOptions}
        paginated={paginatedOptions}
        sortBy={sortBy}
        myBooks={_myBooksList}
        consensusOptions={consensusOptions}
      />
    </View>
  );
}

// ─── Render helpers (kept outside the component for clarity) ───────────────

function renderHeader({ fileInputRef, handleFilePick, runVerify, hasData }) {
  return (
    <LabHeader
      icon="flask-outline"
      title="Results Lab"
      question="Why did this card sort or filter this way?"
      how={'Load a capture, then use the real results page. Build 9 — hard-refresh (Cmd+Shift+R) if you don\'t see it.'}
    >
      <TouchableOpacity onPress={() => fileInputRef.current?.click()} style={styles.primaryBtn} activeOpacity={0.8}>
        <Ionicons name="document-attach-outline" size={14} color={uiVar('adm-ink', '#fff')} />
        <Text style={styles.primaryBtnText}>Pick fixture file</Text>
      </TouchableOpacity>
      <TouchableOpacity onPress={runVerify} style={[styles.primaryBtn, { backgroundColor: '#4ade80' }]} activeOpacity={0.8} disabled={!hasData}>
        <Ionicons name="checkmark-circle-outline" size={14} color="#0b0f1a" />
        <Text style={[styles.primaryBtnText, { color: '#0b0f1a' }]}>Verify sort + filters</Text>
      </TouchableOpacity>
      <input ref={fileInputRef} type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={handleFilePick} />
    </LabHeader>
  );
}

function renderLoaderRow({ fixturePath, setFixturePath, handleLoadByPath, loading, loadError }) {
  return (
    <View style={{ gap: 4 }}>
      <View style={styles.loaderRow}>
        <TextInput
          style={styles.pathInput}
          value={fixturePath}
          onChangeText={setFixturePath}
          placeholder="website/public/ only — use Pick fixture file for scripts/output/"
          placeholderTextColor="rgba(255,255,255,0.3)"
        />
        <TouchableOpacity onPress={handleLoadByPath} style={styles.secondaryBtn} activeOpacity={0.8}>
          <Text style={styles.secondaryBtnText}>Load by path</Text>
        </TouchableOpacity>
        {loading && <ActivityIndicator size="small" color="#A259FF" />}
        {loadError && <Text style={styles.errorText} numberOfLines={3}>{loadError}</Text>}
      </View>
      <Text style={styles.muted}>
        E2E fixtures in scripts/output/ are not served by the dev server. Use <Text style={{ fontWeight: '700' }}>Pick fixture file</Text> to load them from disk.
      </Text>
    </View>
  );
}

function renderCaptureRow({ capturePrompt, setCapturePrompt, handleCapture, capturing, captureError }) {
  return (
    <View style={styles.loaderRow}>
      <Ionicons name="flash-outline" size={14} color="rgba(255,255,255,0.4)" />
      <TextInput
        style={styles.pathInput}
        value={capturePrompt}
        onChangeText={setCapturePrompt}
        placeholder="Capture fresh: enter a live query (e.g. 'best bets tonight')"
        placeholderTextColor="rgba(255,255,255,0.3)"
      />
      <TouchableOpacity onPress={handleCapture} style={[styles.secondaryBtn, { borderColor: 'rgba(96,165,250,0.5)' }]} activeOpacity={0.8} disabled={capturing}>
        <Text style={[styles.secondaryBtnText, { color: '#60a5fa' }]}>{capturing ? 'Capturing…' : 'Capture live'}</Text>
      </TouchableOpacity>
      {capturing && <ActivityIndicator size="small" color="#60a5fa" />}
      {captureError && <Text style={styles.errorText} numberOfLines={2}>{captureError}</Text>}
    </View>
  );
}

function renderControls(p) {
  return (
    <View style={styles.controlsRow}>
      <View style={styles.controlGroup}>
        <Text style={styles.controlLabel}>Query</Text>
        <select
          value={p.queryIndex}
          onChange={(e) => { p.setQueryIndex(Number(e.target.value)); p.setCurrentPage(1); p.setVerifyResult(null); }}
          style={styles.select}
        >
          {p.fixture.availableQueries.map((q) => (
            <option key={q.index} value={q.index}>#{q.index}: {formatQueryLabel(q.q, `Query ${q.index + 1}`)} ({q.count} items)</option>
          ))}
        </select>
      </View>
      <View style={styles.controlGroup}>
        <Text style={styles.controlLabel}>My Books (comma-separated)</Text>
        <TextInput style={styles.textInput} value={p.myBooksInput} onChangeText={p.setMyBooksInput} placeholder="DraftKings, FanDuel" placeholderTextColor="rgba(255,255,255,0.3)" />
      </View>
      <View style={styles.controlGroup}>
        <Text style={styles.controlLabel}>Books mode</Text>
        <select value={p.booksViewOverride ?? p.defaultBookSort} onChange={(e) => p.setBooksViewOverride(e.target.value)} style={styles.select}>
          <option value="ev">All Books (ev)</option>
          <option value="mybooks">My Books</option>
        </select>
      </View>
      <View style={styles.controlGroup}>
        <Text style={styles.controlLabel}>Preferred sportsbook</Text>
        <TextInput style={styles.textInput} value={p.preferredSportsbook || ''} onChangeText={(t) => p.setPreferredSportsbook(t || null)} placeholder="(none)" placeholderTextColor="rgba(255,255,255,0.3)" />
      </View>
      <View style={styles.controlGroup}>
        <Text style={styles.controlLabel}>Reference time (ISO)</Text>
        <TextInput style={styles.textInput} value={p.referenceTime || ''} onChangeText={p.setReferenceTime} placeholder="(use fixture)" placeholderTextColor="rgba(255,255,255,0.3)" />
      </View>
      <View style={styles.controlGroup}>
        <Text style={styles.controlLabel}>Sort mode (lab can set ANY value)</Text>
        <select value={p.sortBy} onChange={(e) => p.setSortBy(e.target.value)} style={styles.select}>
          {LAB_SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </View>
      <View style={styles.controlGroup}>
        <Text style={styles.controlLabel}>Pipeline toggles</Text>
        <View style={{ flexDirection: 'row', gap: 12 }}>
          <label style={styles.checkboxLabel}>
            <input type="checkbox" checked={p.disableMinBooksFilter} onChange={(e) => p.setDisableMinBooksFilter(e.target.checked)} />
            <Text style={styles.checkboxText}> disable min-books</Text>
          </label>
          <label style={styles.checkboxLabel}>
            <input type="checkbox" checked={p.disableMyBooksPriority} onChange={(e) => p.setDisableMyBooksPriority(e.target.checked)} />
            <Text style={styles.checkboxText}> disable My Books priority</Text>
          </label>
          <label style={styles.checkboxLabel}>
            <input type="checkbox" checked={p.disableDateKeywordFilter} onChange={(e) => p.setDisableDateKeywordFilter(e.target.checked)} />
            <Text style={styles.checkboxText}> disable tonight/today date filter</Text>
          </label>
          <label style={styles.checkboxLabel}>
            <input type="checkbox" checked={p.disableRelevanceFilter} onChange={(e) => p.setDisableRelevanceFilter(e.target.checked)} />
            <Text style={styles.checkboxText}> disable query-relevance filter</Text>
          </label>
        </View>
      </View>
    </View>
  );
}

function renderSortWarning({ sortBy }) {
  if (HANDLED_SORT_MODES.has(sortBy)) return null;
  return (
    <View style={styles.warnBanner}>
      <Ionicons name="warning-outline" size={14} color="#fbbf24" />
      <Text style={styles.warnText}>
        '{sortBy}' has NO case in useFilteredAndSortedOptions switch (and no default:).
        The list keeps its pre-switch order — clicking this sort does nothing.
      </Text>
    </View>
  );
}

function renderPipelineStats({ loadedQuery, loadPipelineError, referenceTime, totalFilteredCount, filterStages }) {
  if (loadPipelineError) {
    return (
      <View style={styles.errorBanner}>
        <Ionicons name="alert-circle-outline" size={14} color="#f87171" />
        <Text style={styles.errorBannerText}>Pipeline load failed: {loadPipelineError}</Text>
      </View>
    );
  }
  if (!loadedQuery) return null;
  const raw = loadedQuery.rawItems?.length ?? 0;
  const transformed = loadedQuery.transformedCount ?? loadedQuery.fetchedOptions?.length ?? 0;
  const hydrated = loadedQuery.fetchedOptions?.length ?? 0;
  if (raw === 0) return null;
  const refLabel = referenceTime ? new Date(referenceTime).toLocaleString() : '(not set — games may be filtered as stale)';
  const stageLine = Array.isArray(filterStages) && filterStages.length > 0
    ? ' Filter stages: ' + filterStages.map((s) => `${s.name}=${s.count}`).join(' → ') + '.'
    : '';
  const wiped = hydrated > 0 && (totalFilteredCount === 0);
  return (
    <View style={wiped ? styles.warnBanner : styles.infoBanner}>
      <Ionicons name={wiped ? 'warning-outline' : 'information-circle-outline'} size={14} color={wiped ? '#fbbf24' : '#60a5fa'} />
      <Text style={wiped ? styles.warnText : styles.infoText}>
        Pipeline: {raw} raw → {transformed} transformed → {hydrated} hydrated cards → {totalFilteredCount ?? 0} after display filters.
        Reference time: {refLabel}.{stageLine}
        {wiped && ' Display filters emptied the list — look for the stage that drops to 0 (often afterDateKeyword if the query says tonight/today but games are on another calendar day).'}
      </Text>
    </View>
  );
}

function renderVerifyPanel({ verifyResult }) {
  if (!verifyResult) return null;
  const allChecks = [...(verifyResult.sort?.checks || []), ...(verifyResult.filter?.checks || [])];
  const passCount = allChecks.filter(c => c.pass).length;
  const failCount = allChecks.length - passCount;
  return (
    <View style={styles.verifyPanel}>
      <View style={styles.verifyHeader}>
        <Text style={styles.verifyTitle}>Verification results</Text>
        <Text style={[styles.verifySummary, { color: failCount === 0 ? '#4ade80' : '#f87171' }]}>
          {passCount} pass · {failCount} fail
        </Text>
      </View>
      <ScrollView style={{ maxHeight: 280 }} contentContainerStyle={{ gap: 6 }}>
        {allChecks.map((c, i) => (
          <View key={i} style={[styles.checkRow, !c.pass && c.severity === 'error' && styles.checkRowFail]}>
            <Ionicons
              name={c.pass ? 'checkmark-circle' : c.severity === 'info' ? 'information-circle' : 'close-circle'}
              size={13}
              color={c.pass ? '#4ade80' : c.severity === 'info' ? '#60a5fa' : '#f87171'}
            />
            <View style={{ flex: 1 }}>
              <Text style={styles.checkMessage}>{c.message}</Text>
              {c.violations?.length > 0 && (
                <View style={{ marginTop: 4, paddingLeft: 4 }}>
                  {c.violations.slice(0, 5).map((v, j) => (
                    <Text key={j} style={styles.violationText}>• {v.message}</Text>
                  ))}
                  {c.totalViolations > c.violations.length && (
                    <Text style={styles.violationText}>… and {c.totalViolations - c.violations.length} more</Text>
                  )}
                </View>
              )}
              {c.dropped?.length > 0 && (
                <View style={{ marginTop: 4, paddingLeft: 4 }}>
                  <Text style={styles.violationText}>Dropped (should be present):</Text>
                  {c.dropped.slice(0, 3).map((d, j) => <Text key={j} style={styles.violationText}>• {d}</Text>)}
                </View>
              )}
              {c.extra?.length > 0 && (
                <View style={{ marginTop: 4, paddingLeft: 4 }}>
                  <Text style={styles.violationText}>Extra (should be filtered out):</Text>
                  {c.extra.slice(0, 3).map((d, j) => <Text key={j} style={styles.violationText}>• {d}</Text>)}
                </View>
              )}
            </View>
          </View>
        ))}
      </ScrollView>
    </View>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container: { flex: 1, gap: 12, padding: 16 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  title: { ...ADM_DISPLAY, letterSpacing: uiVar('adm-display-track', 'normal'), color: uiVar('adm-ink', '#fff'), fontSize: 20, fontWeight: '700' },
  subtitle: { color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 2 },
  primaryBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: uiVar('adm-accent-fill', '#A259FF'), paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8 },
  primaryBtnText: { color: uiVar('adm-ink', '#fff'), fontSize: 13, fontWeight: '600' },
  loaderRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  pathInput: { flex: 1, minWidth: 280, color: uiVar('adm-ink', '#fff'), backgroundColor: 'rgba(255,255,255,0.05)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8, fontSize: 13 },
  secondaryBtn: { borderWidth: 1, borderColor: 'rgba(255,255,255,0.2)', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8, backgroundColor: 'rgba(255,255,255,0.04)' },
  secondaryBtnText: { color: uiVar('adm-ink', '#fff'), fontSize: 13, fontWeight: '600' },
  errorText: { color: '#f87171', fontSize: 12 },
  controlsRow: { flexDirection: 'row', gap: 16, flexWrap: 'wrap', backgroundColor: 'rgba(255,255,255,0.03)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', borderRadius: 10, padding: 12 },
  controlGroup: { flexDirection: 'column', gap: 4, minWidth: 180 },
  controlLabel: { color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 },
  textInput: { color: uiVar('adm-ink', '#fff'), backgroundColor: 'rgba(255,255,255,0.05)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', borderRadius: 6, paddingHorizontal: 10, paddingVertical: 6, fontSize: 13, minWidth: 180 },
  select: { color: uiVar('adm-ink', '#fff'), backgroundColor: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: 6, fontSize: 13, minWidth: 180, height: 32 },
  checkboxLabel: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  checkboxText: { color: 'rgba(255,255,255,0.7)', fontSize: 12 },
  warnBanner: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: 'rgba(251,191,36,0.08)', borderWidth: 1, borderColor: 'rgba(251,191,36,0.4)', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8 },
  warnText: { color: '#fbbf24', fontSize: 12, flex: 1 },
  infoBanner: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: 'rgba(96,165,250,0.08)', borderWidth: 1, borderColor: 'rgba(96,165,250,0.35)', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8 },
  infoText: { color: '#93c5fd', fontSize: 12, flex: 1 },
  errorBanner: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: 'rgba(248,113,113,0.08)', borderWidth: 1, borderColor: 'rgba(248,113,113,0.4)', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8 },
  errorBannerText: { color: '#f87171', fontSize: 12, flex: 1 },
  verifyPanel: { backgroundColor: 'rgba(255,255,255,0.03)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', borderRadius: 10, padding: 12 },
  verifyHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  verifyTitle: { color: uiVar('adm-ink', '#fff'), fontSize: 14, fontWeight: '700' },
  verifySummary: { fontSize: 13, fontWeight: '700' },
  checkRow: { flexDirection: 'row', gap: 8, padding: 8, borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.02)' },
  checkRowFail: { backgroundColor: 'rgba(248,113,113,0.06)' },
  checkMessage: { color: 'rgba(255,255,255,0.85)', fontSize: 12, flex: 1 },
  violationText: { color: 'rgba(255,255,255,0.5)', fontSize: 11 },
  previewArea: { gap: 6 },
  sectionLabel: { color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 },
  previewBox: { borderWidth: 1, borderColor: uiVar('adm-accent-wash-30', 'rgba(162,89,255,0.3)'), borderRadius: 10, overflow: 'hidden', backgroundColor: uiVar('adm-accent-wash-3', 'rgba(162,89,255,0.03)'), minHeight: 400 },
  muted: { color: 'rgba(255,255,255,0.5)', fontSize: 13, marginTop: 8 },
});
